import type { Route, Routes } from '@angular/router';
import type { AppRole } from '@core/api/me';
import { NavIconName } from './nav-icon/nav-icon-sprite';



/**
 * Метаданные маршрута для меню. Описываются в `data` маршрута:
 *
 * ```ts
 * data: { nav: { section: 'Аналитика и отчетность', label: 'Аналитика', icon: 'analytics' } }
 * ```
 *
 * Пункт меню появляется только при заполненном `nav`, поэтому служебные маршруты
 * (`/forbidden`, `**`) в меню не попадают. Роли берутся из `data.roles` — их же
 * проверяет `roleGuard`, поэтому список доступных разделов не расходится с правами.
 */
export interface NavRouteData {
    /** Заголовок раздела; `null` — пункт верхнего уровня без заголовка. */
    readonly section: string | null;
    readonly label: string;
    readonly icon: NavIconName;
    /** Главный раздел: рисуется отдельной плашкой над списком. */
    readonly primary?: boolean;
    readonly order?: number;
    /** Текст кнопки «Создать...» в левом сайдбаре. Если задан — над разделом появится красная кнопка. */
    readonly create?: string;
}

/** Ссылка меню. */
export interface NavLink {
    readonly label: string;
    readonly icon: NavIconName;
    readonly path: string;
}

/** Раздел меню: заголовок (если есть) и его ссылки. */
export interface NavSection {
    readonly title: string | null;
    readonly items: readonly NavLink[];
}

/** Готовое меню сайдбара: главный раздел отдельно, остальное — разделами. */
export interface NavMenu {
    readonly primary: NavLink | null;
    readonly sections: readonly NavSection[];
}

interface RouteData {
    readonly nav?: NavRouteData;
    readonly roles?: readonly AppRole[];
}

interface NavNode extends NavLink {
    /** Раздел пункта: `null` — верхний уровень. */
    readonly section: string | null;
    readonly order: number;
    readonly primary: boolean;
}

const WILDCARD = '**';
const LAST_ORDER = Number.MAX_SAFE_INTEGER;

/**
 * Меню строится из конфигурации роутера, чтобы список разделов и маршруты
 * не расходились: добавили маршрут с `data.nav` — появился пункт меню.
 */
export function buildNavSections(
    routes: Routes,
    hasAnyRole: (roles: readonly AppRole[]) => boolean,
): NavMenu {
    const nodes = collect(routes, '', null, hasAnyRole);
    const primary = nodes.find((node) => node.primary) ?? null;

    return { primary: primary && toLink(primary), sections: toSections(nodes, primary) };
}

/**
 * Обходит маршруты и собирает пункты с `data.nav`. Маршрут с детьми — контейнер
 * раздела: сам он ссылкой не становится, а его `section` наследуют дети, у которых
 * раздел не задан (например, «Администрирование» → «Пользователи»).
 */
function collect(
    routes: Routes,
    parentPath: string,
    parentSection: string | null,
    hasAnyRole: (roles: readonly AppRole[]) => boolean,
): NavNode[] {
    const nodes: NavNode[] = [];

    for (const route of routes) {
        const data = readData(route);

        if (route.path === WILDCARD || (data?.roles && !hasAnyRole(data.roles))) {
            continue;
        }

        const path = joinPath(parentPath, route.path);
        const nav = data?.nav;
        const section = nav?.section === undefined ? parentSection : nav.section;
        const children = route.children
            ? collect(route.children, path, section ?? nav?.label ?? parentSection, hasAnyRole)
            : [];

        // Маршрут без nav — обвязка (Shell, componentless-группы): поднимаем его детей.
        if (!nav) {
            nodes.push(...children);
            continue;
        }

        // Маршрут с nav и детьми — контейнер раздела: ссылкой не рисуется.
        if (children.length > 0) {
            nodes.push(...children);
            continue;
        }

        nodes.push({
            label: nav.label,
            icon: nav.icon,
            path,
            section,
            order: nav.order ?? LAST_ORDER,
            primary: nav.primary ?? false,
        });
    }

    return nodes.sort((left, right) => left.order - right.order);
}

function toSections(nodes: readonly NavNode[], primary: NavNode | null): readonly NavSection[] {
    const buckets = new Map<string | null, NavLink[]>();

    for (const node of nodes) {
        if (node === primary) continue;

        const items = buckets.get(node.section);

        if (items) {
            items.push(toLink(node));
            continue;
        }

        buckets.set(node.section, [toLink(node)]);
    }

    return [...buckets].map(([title, items]) => ({ title, items }));
}

function toLink(node: NavNode): NavLink {
    return { label: node.label, icon: node.icon, path: node.path };
}

function joinPath(parent: string, path: string | undefined): string {
    if (!path) return parent;
    return parent ? `${parent}/${path}` : `/${path}`;
}

function readData(route: Route): RouteData | undefined {
    return route.data as RouteData | undefined;
}
